'use client'

import type { ReactNode, ComponentPropsWithoutRef } from 'react'
import { useEffect, useRef, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'

import clsx from 'clsx'
import { Image } from '@systhemaui/next'

type ModalProps = Omit<
  ComponentPropsWithoutRef<'div'>,
  'children' | 'open' | 'className' | 'thumbnail' | 'onClose'
> & {
  children: ReactNode
  className?: string
  open: boolean
  size: 'xs' | 'sm' | 'md' | 'lg'
  contentType?: 'article' | 'video' | 'creator'
  thumbnail?: string | undefined
  onClose: () => void
}

const emptySubscribe = () => () => {}

const Modal = ({
  children,
  open,
  size = 'md',
  contentType = 'article',
  thumbnail,
  onClose,
  className,
}: ModalProps) => {
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const originalScrollStylesRef = useRef<{
    htmlOverflow: string
    bodyOverflow: string
    bodyPaddingRight: string
  } | null>(null)

  // Detect hydration without setState-in-effect
  const isHydrated = useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  )

  // Derive popover support from hydration state (no effect needed)
  const isPopoverSupported =
    isHydrated &&
    typeof HTMLDivElement !== 'undefined' &&
    typeof (HTMLDivElement.prototype as unknown as { showPopover?: () => void }).showPopover ===
      'function'

  // Keep React state in sync with user-initiated closes (e.g., Esc)
  useEffect(() => {
    if (!isHydrated || !isPopoverSupported) return

    const el = popoverRef.current
    if (!el) return

    const handleToggle = () => {
      // When popover is closed (by user), notify parent
      const isOpen = (el as unknown as { matches?: (sel: string) => boolean }).matches?.(
        ':popover-open',
      )
      if (!isOpen) onClose()
    }

    el.addEventListener('toggle', handleToggle as EventListener)
    return () => {
      el.removeEventListener('toggle', handleToggle as EventListener)
    }
  }, [isHydrated, isPopoverSupported, onClose])

  // Imperatively open/close the popover based on `open` prop
  useEffect(() => {
    if (!isHydrated || !isPopoverSupported) return

    const el = popoverRef.current as unknown as {
      showPopover?: () => void
      hidePopover?: () => void
      matches?: (sel: string) => boolean
    } | null
    if (!el) return

    try {
      if (open) {
        if (!el.matches || !el.matches(':popover-open')) el.showPopover?.()
      } else {
        if (el.matches && el.matches(':popover-open')) el.hidePopover?.()
      }
    } catch {
      // no-op; environments without Popover API
    }
  }, [isHydrated, isPopoverSupported, open])

  // Lock page scroll when modal is open; allow only inner content to scroll
  useEffect(() => {
    if (!isHydrated || !isPopoverSupported) return
    if (typeof document === 'undefined') return
    const html = document.documentElement
    const body = document.body

    if (open) {
      if (!originalScrollStylesRef.current) {
        originalScrollStylesRef.current = {
          htmlOverflow: html.style.overflow,
          bodyOverflow: body.style.overflow,
          bodyPaddingRight: body.style.paddingRight,
        }
      }

      const scrollbarWidth = window.innerWidth - html.clientWidth
      html.style.overflow = 'hidden'
      body.style.overflow = 'hidden'
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`
      }

      return () => {
        const prev = originalScrollStylesRef.current
        if (prev) {
          html.style.overflow = prev.htmlOverflow
          body.style.overflow = prev.bodyOverflow
          body.style.paddingRight = prev.bodyPaddingRight
          originalScrollStylesRef.current = null
        }
      }
    } else {
      const prev = originalScrollStylesRef.current
      if (prev) {
        html.style.overflow = prev.htmlOverflow
        body.style.overflow = prev.bodyOverflow
        body.style.paddingRight = prev.bodyPaddingRight
        originalScrollStylesRef.current = null
      }
    }
  }, [isHydrated, isPopoverSupported, open])

  // No need for an outside click handler when using popover="auto" + backdrop;
  // syncing is handled via the `toggle` listener above.

  // Don't render modal if popover is not supported
  if (isHydrated && !isPopoverSupported) {
    return null
  }

  return (
    <>
      {isHydrated && open && typeof document !== 'undefined'
        ? createPortal(
            <div
              role="presentation"
              aria-hidden="true"
              className="fixed inset-0 bg-black/50 backdrop-blur-lg"
              onClick={onClose}
            />,
            document.body,
          )
        : null}
      {isHydrated && (
        <div
          ref={popoverRef}
          popover="manual"
          className={clsx(
            'animate-fadeinup inset-0 m-auto! max-h-screen overflow-visible overflow-x-clip overflow-y-auto bg-transparent p-0 backdrop:bg-black/50 backdrop:backdrop-blur-lg',
            size === 'xs' && 'w-container max-w-96',
            size === 'sm' && 'w-container max-w-160',
            size === 'md' && 'w-container max-w-215',
            size === 'lg' && 'w-container max-w-320',
            className,
          )}
          data-theme="default"
        >
          {/* Floating close button (matches Figma) */}
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="button-primary absolute top-6 right-0 rounded-br-none rounded-bl-none border border-b-0 px-5 py-2.5 shadow-lg shadow-black/10"
          >
            <svg
              className="button-icon"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 -960 960 960"
            >
              <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
            </svg>
            <span className="sr-only!">Close</span>
          </button>
          <div
            className={clsx(
              'modal-content-wrapper bg-layout-base relative mt-16',
              contentType === 'article' && 'max-h-[80dvh] overflow-x-clip overflow-y-auto',
            )}
          >
            {thumbnail && (
              <Image className="h-auto w-full" src={thumbnail} alt="" disableAnimation />
            )}
            {(() => {
              switch (contentType) {
                case 'article':
                  return (
                    children && (
                      <div className="wysiwyg aos-disable-children first:is-[figure]:*:-mx-4 first:is-[figure]:*:-mt-6! first:is-[figure]:*:w-[calc(100%+48px)] md:first:is-[figure]:*:-mx-10 md:first:is-[figure]:*:-mt-12! md:first:is-[figure]:*:w-[calc(100%+80px)] w-full px-4 py-6 md:px-10 md:py-12 [&_figure:first-child_iframe]:aspect-1387/821! [&_figure:first-child_iframe]:h-auto! [&_figure:first-child_iframe]:w-full!">
                        {children}
                      </div>
                    )
                  )
                case 'video':
                  return <div className="wysiwyg aos-disable-children w-full">{children}</div>
                case 'creator':
                  // Placeholder for 'creator' content type, update as needed
                  return <div className="aos-disable-children w-full">{children}</div>
                default:
                  return null
              }
            })()}
          </div>
        </div>
      )}
    </>
  )
}

export default Modal
